/**
 * The axios response-error handler, as a function you can call.
 *
 * It lived inline in main.js, which meant importing it meant mounting the whole
 * app — so the 401 logout path had never been tested, and the 403 path below is
 * new enough to deserve better.
 *
 * Two behaviours:
 *
 *  - **401** → hard logout and back to /login, except on the join/signup routes
 *    where an unauthenticated request is the normal case.
 *  - **403 carrying `X-Authz-Denied`** → re-read the user, then replay the
 *    request once. Permissions are a live database read on the API now, so the
 *    server can start refusing mid-session while this tab still holds a token
 *    whose payload says otherwise; without the replay the user just sees a
 *    button that silently does nothing until they reload.
 *
 * The retry is marked on the request config so a genuine 403 cannot loop.
 */
export function createResponseErrorHandler({ axios, auth, router, store }) {
    return async error => {
        const route = (router && router.currentRoute && router.currentRoute.path)
            ? router.currentRoute.path
            : null
        const invalid_route = !!(route && (route.includes('/join/') || route.includes('/signup')))

        if (!invalid_route && error.response && error.response.status === 401) {
            auth.logout()
            router.replace('/login')
        }

        if (!invalid_route && error.response && error.response.status === 403
            && error.response.headers?.['x-authz-denied'] === '1'
            && error.config && !error.config.__authzRetried) {
            error.config.__authzRetried = true
            try {
                await store.dispatch('userStore/pullLoggedInUserInfo', localStorage.getItem('id_token'))
                error.config.headers = error.config.headers || {}
                error.config.headers.Authorization = axios.defaults.headers.common['Authorization']
                return axios.request(error.config)
            } catch (refreshError) {
                // Fall through to the normal rejection — the 403 was real, or
                // we could not refresh. Either way the caller gets its error.
            }
        }

        return Promise.reject(error.response)
    }
}
